import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Responsive from 'react-responsive';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styles from './index.scss';
import IconArrowLeft from '../../../../components/IconArrowLeft';
import { OLD_MOBILE_BREAKPOINT_MAX, DESKTOP_LG_BREAKPOINT_MIN } from '../../../../../config/consts';

const BreadcrumbsDots = ({
    shouldCrumbsBeHidden,
    changeStateCallback,
    breadCrumbsBar,
    children,
}) => {
    const dotsClasses = classnames({
        [styles.itemLink]: true,
        [styles.dots]: true,
    });

    const dotsHeight = breadCrumbsBar ? breadCrumbsBar.getBoundingClientRect().height : null;

    return (
        <TransitionGroup component={null}>
            {shouldCrumbsBeHidden ? (
                <CSSTransition
                    in={shouldCrumbsBeHidden}
                    timeout={0}
                    onEnter={element => {
                        // eslint-disable-next-line no-param-reassign
                        element.style.height = `${dotsHeight}px`;
                        // eslint-disable-next-line no-param-reassign
                        breadCrumbsBar.style.height = 'auto';
                    }}
                    onEntered={element => {
                        // eslint-disable-next-line no-param-reassign
                        element.style.height = '18px';
                    }}
                    classNames={{
                        enter: styles.dotsEnter,
                        enterDone: styles.dotsEnterDone,
                    }}
                >
                    <div className={dotsClasses} onMouseEnter={changeStateCallback}>
                        ...
                    </div>
                </CSSTransition>
            ) : (
                children.map((child, i) => (
                    <CSSTransition
                        key={i}
                        in={!shouldCrumbsBeHidden}
                        timeout={0}
                        classNames={{
                            enter: styles.itemEnter,
                            exit: styles.itemExit,
                            exitDone: styles.itemExitDone,
                        }}
                    >
                        {child}
                    </CSSTransition>
                ))
            )}
        </TransitionGroup>
    );
};

BreadcrumbsDots.propTypes = {
    shouldCrumbsBeHidden: PropTypes.bool.isRequired,
    changeStateCallback: PropTypes.func.isRequired,
    breadCrumbsBar: PropTypes.object,
};

class Breadcrumbs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            shouldCrumbsBeHidden: true,
        };

        this.breadCrumbsBar = React.createRef();
    }

    renderBreadcrumbItem = ({ url, displayName, displayNameTag, type }, index) => (
        <li
            className={styles.item}
            key={url}
            data-item-name={displayNameTag}
            data-item-type={type}
            itemProp="itemListElement"
            itemScope=""
            itemType="http://schema.org/ListItem"
        >
            <Link className={styles.itemLink} to={url} itemprop="item">
                <span className={styles.itemText} itemProp="name">
                    {displayName}
                </span>
                <meta content={index} itemProp="position"></meta>
            </Link>
        </li>
    );

    renderExternalLink = ({ url, displayName, displayNameTag, type }, index) => (
        <li
            className={styles.item}
            key={url}
            data-item-name={displayNameTag}
            data-item-type={type}
            itemProp="itemListElement"
            itemScope=""
            itemType="http://schema.org/ListItem"
        >
            <a className={styles.itemLink} href={url} itemProp="item">
                <span className={styles.itemText} itemProp="name">
                    {displayName}
                </span>
                <meta content={index} itemProp="position"></meta>
            </a>
        </li>
    );

    renderItem = (item, index) =>
        item.external
            ? this.renderExternalLink(item, index + 1)
            : this.renderBreadcrumbItem(item, index + 1);

    renderBreadcrumbItemsWithDots({ qtyAfterDots }) {
        const breadcrumbsLength = this.props.breadcrumbs.length;
        const [
            itemsBeforeDots,
            itemsUnderDots,
            itemsAfterDots,
            lastItem,
        ] = this.breadCrumbsList.reduce(
            (breadcrumbsArrays, currentBreadcrumb, i) => {
                if (i < 2) {
                    // eslint-disable-next-line no-param-reassign
                    breadcrumbsArrays[0] = [...breadcrumbsArrays[0], currentBreadcrumb];
                } else if (i >= 2 && i < breadcrumbsLength - qtyAfterDots) {
                    // eslint-disable-next-line no-param-reassign
                    breadcrumbsArrays[1] = [...breadcrumbsArrays[1], currentBreadcrumb];
                } else if (i !== breadcrumbsLength - 1) {
                    // eslint-disable-next-line no-param-reassign
                    breadcrumbsArrays[2] = [...breadcrumbsArrays[2], currentBreadcrumb];
                } else {
                    // eslint-disable-next-line no-param-reassign
                    breadcrumbsArrays = [...breadcrumbsArrays, currentBreadcrumb];
                }
                return breadcrumbsArrays;
            },
            [[], [], []],
        );
        return (
            <React.Fragment>
                {itemsBeforeDots.map((item, index) => this.renderItem(item, index))}
                <BreadcrumbsDots
                    shouldCrumbsBeHidden={this.state.shouldCrumbsBeHidden}
                    changeStateCallback={() => this.setState({ shouldCrumbsBeHidden: false })}
                    breadCrumbsBar={this.breadCrumbsBar ? this.breadCrumbsBar.current : null}
                >
                    {itemsUnderDots.map((item, index) => this.renderItem(item, index))}
                </BreadcrumbsDots>
                {itemsAfterDots.map((item, index) => this.renderItem(item, index))}
                <li
                    className={styles.item}
                    key={lastItem.displayName}
                    property="itemListElement"
                    typeof="ListItem"
                >
                    <span className={styles.item_text}>{lastItem.displayName}</span>
                </li>
            </React.Fragment>
        );
    }

    renderBreadcrumbItemsWithoutDots() {
        const lastItemIndex = this.props.breadcrumbs.length - 1;
        return this.breadCrumbsList.map((item, i) => {
            if (lastItemIndex !== i && item.url) {
                return this.renderItem(item, i);
            }

            return (
                <li
                    className={styles.item}
                    key={item.displayName}
                    data-item-name={item.displayNameTag}
                    data-item-type={item.type}
                    property="itemListElement"
                    typeof="ListItem"
                >
                    <span className={styles.item_text}>{item.displayName}</span>
                </li>
            );
        });
    }

    renderBackCrumb() {
        const lastItemIndex = this.props.breadcrumbs.length - 1;
        const backCrumb = this.props.breadcrumbs[lastItemIndex - 1];

        return (
            <li
                className={styles.item}
                property="itemListElement"
                typeof="ListItem"
                data-item-name={backCrumb.displayNameTag}
                data-item-type={backCrumb.type}
            >
                {backCrumb &&
                    (backCrumb.external ? (
                        <a className={styles.itemLink} href={backCrumb.url}>
                            <IconArrowLeft />
                            <span className={styles.item_text}>{backCrumb.displayName}</span>
                        </a>
                    ) : (
                        <Link className={styles.itemLink} to={backCrumb.url}>
                            <IconArrowLeft />
                            <span className={styles.item_text}>{backCrumb.displayName}</span>
                        </Link>
                    ))}
            </li>
        );
    }

    renderBreadcrumbs() {
        const breadcrumbsLength = this.props.breadcrumbs.length;
        const breadcrumbsClassnames = classnames({
            [styles.breadcrumbs]: true,
            [styles.breadcrumbsCollapsed]: this.state.shouldCrumbsBeHidden,
            [styles.breadcrumbsExpanded]: !this.state.shouldCrumbsBeHidden,
            breadcrumbs: true,
        });

        return (
            <ul
                className={breadcrumbsClassnames}
                ref={this.breadCrumbsBar}
                itemScope=""
                itemType="http://schema.org/BreadcrumbList"
            >
                <Responsive maxWidth={OLD_MOBILE_BREAKPOINT_MAX}>
                    {match => {
                        if (match) {
                            return this.renderBackCrumb();
                        }

                        return (
                            <Responsive minWidth={DESKTOP_LG_BREAKPOINT_MIN + 1}>
                                {isMatch => {
                                    if (isMatch && breadcrumbsLength > 5) {
                                        return this.renderBreadcrumbItemsWithDots({
                                            qtyAfterDots: 3,
                                        });
                                    } else if (!isMatch && breadcrumbsLength > 4) {
                                        return this.renderBreadcrumbItemsWithDots({
                                            qtyAfterDots: 2,
                                        });
                                    }
                                    return this.renderBreadcrumbItemsWithoutDots();
                                }}
                            </Responsive>
                        );
                    }}
                </Responsive>
            </ul>
        );
    }

    render() {
        const { extraClass } = this.props;
        const breadcrumbsClasses = classnames({
            'se-breadcrumbs': true,
            [extraClass]: !!extraClass,
            [styles.breadcrumbsWrapper]: true,
        });
        let timerID = null;
        const breadCrumbsBar = this.breadCrumbsBar.current;
        this.breadCrumbsList = this.props.breadcrumbs.map((item, i) => {
            // eslint-disable-next-line no-param-reassign
            if (i < this.props.external) item.external = true;
            return item;
        });
        const breadCrumbAttr = this.props.breadcrumbs.map(item => item.displayNameTag);

        return (
            <div className={breadcrumbsClasses} data-breadcrumbs={breadCrumbAttr}>
                <nav
                    className={styles.breadcrumbsContainer}
                    onMouseEnter={() => {
                        clearTimeout(timerID);
                        if (breadCrumbsBar && breadCrumbsBar.style) {
                            breadCrumbsBar.style.height = 'auto';
                        }
                    }}
                    onMouseLeave={() => {
                        if (breadCrumbsBar && breadCrumbsBar.style) {
                            breadCrumbsBar.style.height = `${
                                breadCrumbsBar.getBoundingClientRect().height
                            }px`;
                        }
                        timerID = setTimeout(() => {
                            this.setState({ shouldCrumbsBeHidden: true });
                        }, 400);
                    }}
                    aria-label="breadcrumb"
                >
                    {this.props.breadcrumbs ? (
                        <div
                            className={styles.breadcrumbsRow}
                            onClick={() => {
                                this.props.callback && this.props.callback(true);
                            }}
                        >
                            {this.renderBreadcrumbs()}
                        </div>
                    ) : null}
                </nav>
            </div>
        );
    }
}

Breadcrumbs.propTypes = {
    callback: PropTypes.func,
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            displayName: PropTypes.string.isRequired,
            displayNameTag: PropTypes.string,
            type: PropTypes.string,
            external: PropTypes.bool,
        }),
    ).isRequired,
    external: PropTypes.number,
    extraClass: PropTypes.object,
};

export default Breadcrumbs;
